/**
 * Copyright (c) 2010-2020 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import {CollectionViewer} from '@angular/cdk/collections';
import {DataSource} from '@angular/cdk/table';
import {Observable, of, Subject} from 'rxjs';
import {catchError, finalize, map, tap} from 'rxjs/operators';
import {LoggerService} from '../../shared/services/logger.service';
import {SearchService} from '../services/search.service';
import {SearchDefinition, SearchResults} from '../state/search.model';

export class SearchResultsContentDataSource extends DataSource<any> {
    public loading$: Observable<boolean>;
    public count$: Observable<number>;
    public totals$: Observable<any[]>;
    public averages$: Observable<any[]>;
    public results$: Observable<any[]>;
    public allResults$: Observable<any[]>;

    private resultsSubject = new Subject<any[]>();
    private allResultsSubject = new Subject<any[]>();
    private resultCountSubject = new Subject<number>();
    private loadingSubject = new Subject<boolean>();
    private totalsSubject = new Subject<any[]>();
    private averagesSubject = new Subject<any[]>();

    constructor(
        private logger: LoggerService,
        private searchService: SearchService,
        private forPrint: boolean,
    ) {
        super();
        this.loading$ = this.loadingSubject.asObservable();
        this.count$ = this.resultCountSubject.asObservable();
        this.totals$ = this.totalsSubject.asObservable();
        this.averages$ = this.averagesSubject.asObservable();
        this.results$ = this.resultsSubject.asObservable();
        this.allResults$ = this.allResultsSubject.asObservable();
    }

    public connect(collectionViewer: CollectionViewer): Observable<any> {
        return this.forPrint ? this.allResults$ : this.results$;
    }

    public disconnect(collectionViewer: CollectionViewer): void {
        this.resultsSubject.complete();
        this.allResultsSubject.complete();
        this.resultCountSubject.complete();
        this.loadingSubject.complete();
        this.totalsSubject.complete();
        this.averagesSubject.complete();
    }

    /**
     * Call the Search Service and get the results.
     * Note that the page index is 1-based on the api side, but 0-based in the UI, so we add one here and
     * here only.
     *
     * @param searchName
     * @param searchDefinition
     * @param searchParams
     * @param page
     * @param pageLimit
     * @param sortColumn
     * @param sortDirection
     */
    public loadSearchResults(
        searchName: string,
        searchDefinition: SearchDefinition,
        searchParams: any,
        page?: number,
        pageLimit?: number,
        sortColumn?: string,
        sortDirection?: string,
    ) {
        this.loadingSubject.next(true);
        this.searchService.runSearch(
            searchName,
            searchDefinition,
            searchParams,
            (page || 0) + 1, // paginator page is 0-based, api is 1-based
            pageLimit || 10,
            sortColumn || searchDefinition.resultFields[0].name,
            sortDirection || 'asc',
        ).pipe(
            tap((result: SearchResults) => {
                this.resultCountSubject.next((result).count);
                this.totalsSubject.next(result.totals);
                this.averagesSubject.next(result.averages);
            }),
            map((result: SearchResults) => result.data),
            catchError((err) => of(err)),
            finalize(() => this.loadingSubject.next(false)),
        ).subscribe((results) => this.resultsSubject.next(results));
    }

    public loadAllSearchResults(
        searchName: string,
        searchDefinition: SearchDefinition,
        searchParams: any,
        sortColumn?: string,
        sortDirection?: string,
    ) {
        this.searchService.runSearchForPrint(
            searchName,
            searchDefinition,
            searchParams,
            sortColumn || searchDefinition.resultFields[0].name,
            sortDirection || 'asc',
        ).pipe(
            tap((result: SearchResults) => {
                this.resultCountSubject.next((result).count);
                this.totalsSubject.next(result.totals);
                this.averagesSubject.next(result.averages);
            }),
            map((result: SearchResults) => result.data),
        ).subscribe((results) => {
            return this.allResultsSubject.next(results);
        });
    }
}
